/**
 * Price cause tags (spec §5.5, §12.3): the chip beside a market price. The price delta compares the unit
 * price at the current fill with the unit price at the 3-day baseline fill; at |delta| ≥ wares.json
 * `market.causeMinDelta` (10%) the ware gets a tag, attributed like a Wire effect (`wire/causes.ts`) and
 * rendered from wire.json `priceCauses`, e.g. "+24% supply shock: convoy lost near Varos".
 *
 * The delta uses the station's sell-side curve `S(fill) = base × lerp(maxMult, minMult, fill)` (spec §5.1).
 * The base cancels out, so only the curve's multipliers matter (the buy side's ×1.1 markup cancels too).
 * Built with the station view (at most 1 Hz), so it may allocate.
 */
import type { StrategicData } from '../data'
import { buys, fillOf, sells, sw } from '../economy/stock'
import type { CauseKindName, PriceCause, PriceCauseTag } from '../types'
import { attributeCause, causeFields, createCause, type Cause, type WireContext } from '../wire/causes'
import { formatPct, priceCauseTemplate, render } from '../wire/templates'

/** The sell-side curve multiplier at `fill` (clamped to 0..1): maxMult at 0, minMult at 1. */
export function curveMult(data: StrategicData, fill: number): number {
  const m = data.wares.market
  const f = fill < 1 ? (fill > 0 ? fill : 0) : 1
  return m.maxMult + (m.minMult - m.maxMult) * f
}

/** Signed unit-price change from the baseline fill to `fillNow` (+0.24 = 24% above the baseline price). */
export function priceDelta(data: StrategicData, fillNow: number, baselineFill: number): number {
  const b = curveMult(data, baselineFill)
  return (curveMult(data, fillNow) - b) / b
}

/** The chip tag of each cause kind. The unexplained rise is a supply shock; demand jumps are "demand". */
export const PRICE_CAUSE_TAG_OF = {
  lostCarrier: 'convoyLost',
  upstreamStarved: 'inputShortage',
  raidNearby: 'raid',
  demandJump: 'demand',
  playerDump: 'dump',
  strongDemand: 'supplyShock',
  oversupply: 'oversupply',
} as const satisfies Record<CauseKindName, PriceCauseTag>

const scratch: Cause = createCause()

/**
 * The price cause of `ware` at `station` (spec §12.3), or null when the station doesn't trade the ware or
 * the price is within `causeMinDelta` of its baseline.
 */
export function priceCause(ctx: WireContext, station: number, ware: number): PriceCause | null {
  const st = ctx.stations
  if (station < 0 || station >= st.count || !(buys(st, station, ware) || sells(st, station, ware))) return null
  const delta = priceDelta(ctx.data, fillOf(st, station, ware), st.baseline[sw(station, ware)])
  if (!(Math.abs(delta) >= ctx.data.wares.market.causeMinDelta)) return null
  const cause = attributeCause(ctx, { side: delta > 0 ? 'short' : 'glut', station, ware }, scratch)
  const tag = PRICE_CAUSE_TAG_OF[cause.kind]
  const deltaPct = Math.round(delta * 100)
  return { tag, deltaPct, text: render(priceCauseTemplate(ctx.data, tag), { ...causeFields(ctx, cause), pct: formatPct(deltaPct) }) }
}
